import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
  Grid,
  Tooltip,
  LinearProgress
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import WorkIcon from '@mui/icons-material/Work';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { AuthContext } from '../../../context/AuthContext';

const AsignacionIA = ({ 
  cuadranteId, 
  servicioId, 
  clienteId,
  turnos = [],
  onAsignacionComplete,
  onClose 
}) => {
  const { currentService, adminData } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(false);
  const [loadingVigilantes, setLoadingVigilantes] = useState(true);
  const [vigilantesDisponibles, setVigilantesDisponibles] = useState([]);
  const [turnosSinAsignar, setTurnosSinAsignar] = useState([]);
  const [sugerenciasIA, setSugerenciasIA] = useState([]);
  const [asignacionesConfirmadas, setAsignacionesConfirmadas] = useState([]);
  const [error, setError] = useState(null);
  const [iaStatus, setIaStatus] = useState('idle');
  const [processingStep, setProcessingStep] = useState('');

  useEffect(() => {
    if (currentService?.id && servicioId) {
      cargarVigilantesDelServicio();
    }
  }, [currentService?.id, servicioId]);

  useEffect(() => {
    if (turnos && turnos.length > 0) {
      const sinAsignar = turnos.filter(t => 
        t.sinAsignar || !t.vigilante || t.vigilante === '' || t.title === 'Sin Asignar'
      );
      setTurnosSinAsignar(sinAsignar);
    }
  }, [turnos]);

  const cargarVigilantesDelServicio = async () => {
    setLoadingVigilantes(true);
    try {
      const vigilantesRef = collection(db, 'servicios', currentService.id, 'vigilantes');
      
      const q = query(
        vigilantesRef,
        where('borrado', '==', false)
      );
      
      const snapshot = await getDocs(q);
      const vigilantesData = [];
      
      snapshot.forEach(doc => {
        const data = doc.data();
        const tiendas = data.tiendas || [];
        const tieneServicio = tiendas.some(t => 
          t.numero?.toString() === servicioId?.toString() ||
          t === servicioId
        );
        
        if (tieneServicio || tiendas.length === 0) {
          vigilantesData.push({
            id: doc.id,
            ...data,
            nombreCompleto: `${data.nombre || ''} ${data.apellidos || ''}`.trim() || data.email,
            codigoPersona: data.persona,
            serviciosAsignados: tiendas.length,
            tieneServicioActual: tieneServicio
          });
        }
      });

      vigilantesData.sort((a, b) => {
        if (a.tieneServicioActual && !b.tieneServicioActual) return -1;
        if (!a.tieneServicioActual && b.tieneServicioActual) return 1;
        return 0;
      });

      setVigilantesDisponibles(vigilantesData);
      console.log('Vigilantes cargados:', vigilantesData.length);
    } catch (err) {
      console.error('Error cargando vigilantes:', err);
      setError('Error al cargar vigilantes: ' + err.message);
    } finally {
      setLoadingVigilantes(false);
    }
  };

  const generarSugerenciasIA = async () => {
    if (turnosSinAsignar.length === 0) {
      setError('No hay turnos sin asignar');
      return;
    }
    
    if (vigilantesDisponibles.length === 0) {
      setError('No hay vigilantes disponibles para este servicio');
      return;
    }

    setLoading(true);
    setIaStatus('processing');
    setError(null);
    setSugerenciasIA([]);

    try {
      setProcessingStep('Analizando turnos y disponibilidad...');
      await new Promise(resolve => setTimeout(resolve, 800));

      setProcessingStep('Consultando modelo de IA...');
      
      const datosParaIA = {
        turnos: turnosSinAsignar.map(t => ({
          id: t.id,
          fecha: t.start ? new Date(t.start).toISOString().split('T')[0] : t.fecha,
          horaInicio: t.inicioTurno || (t.start ? new Date(t.start).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : ''),
          horaFin: t.finTurno || (t.end ? new Date(t.end).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : ''),
          turno: t.turno || 'Regular'
        })),
        vigilantes: vigilantesDisponibles.map(v => ({
          id: v.id,
          nombre: v.nombreCompleto,
          codigoPersona: v.codigoPersona,
          tieneServicio: v.tieneServicioActual,
          serviciosAsignados: v.serviciosAsignados
        })),
        servicioId: servicioId,
        cuadranteId: cuadranteId
      };

      let sugerencias = [];

      try {
        const generarAsignacionesIA = httpsCallable(functions, 'generarAsignacionesIA');
        const resultado = await generarAsignacionesIA(datosParaIA);
        
        if (resultado.data?.sugerencias) {
          sugerencias = resultado.data.sugerencias;
        }
      } catch (functionError) {
        console.log('Cloud Function no disponible, usando algoritmo local:', functionError.message);
        
        setProcessingStep('Generando asignaciones optimizadas...');
        await new Promise(resolve => setTimeout(resolve, 600));
        
        sugerencias = generarAsignacionesLocales(datosParaIA);
      }

      setProcessingStep('Preparando resultados...');
      await new Promise(resolve => setTimeout(resolve, 400));

      setSugerenciasIA(sugerencias);
      setIaStatus('completed');
      
    } catch (err) {
      console.error('Error generando sugerencias:', err);
      setError('Error al generar sugerencias: ' + err.message);
      setIaStatus('error');
    } finally {
      setLoading(false);
      setProcessingStep('');
    }
  };

  const generarAsignacionesLocales = (datos) => {
    const { turnos, vigilantes } = datos;
    const sugerencias = [];
    const asignacionesPorVigilante = {};

    vigilantes.forEach(v => {
      asignacionesPorVigilante[v.id] = 0;
    });

    const vigilantesConServicio = vigilantes.filter(v => v.tieneServicio);
    const vigilantesPool = vigilantesConServicio.length > 0 ? vigilantesConServicio : vigilantes;

    turnos.forEach((turno, index) => {
      const vigilanteOrdenado = [...vigilantesPool].sort((a, b) => {
        const asigA = asignacionesPorVigilante[a.id] || 0;
        const asigB = asignacionesPorVigilante[b.id] || 0;
        if (asigA !== asigB) return asigA - asigB;
        if (a.tieneServicio && !b.tieneServicio) return -1;
        if (!a.tieneServicio && b.tieneServicio) return 1;
        return 0;
      });

      const vigilanteSeleccionado = vigilanteOrdenado[0];
      
      if (vigilanteSeleccionado) {
        asignacionesPorVigilante[vigilanteSeleccionado.id]++;
        
        sugerencias.push({
          turnoId: turno.id,
          turnoInfo: {
            fecha: turno.fecha,
            horaInicio: turno.horaInicio,
            horaFin: turno.horaFin,
            turno: turno.turno
          },
          vigilanteId: vigilanteSeleccionado.id,
          vigilanteNombre: vigilanteSeleccionado.nombre,
          codigoPersona: vigilanteSeleccionado.codigoPersona,
          confianza: vigilanteSeleccionado.tieneServicio ? 95 : 75,
          razon: vigilanteSeleccionado.tieneServicio 
            ? 'Vigilante asignado al servicio con menor carga de trabajo'
            : 'Vigilante disponible con menor carga de trabajo'
        });
      }
    });

    return sugerencias;
  };

  const confirmarAsignacion = (sugerencia) => {
    setAsignacionesConfirmadas(prev => [...prev, sugerencia]);
    setSugerenciasIA(prev => prev.filter(s => s.turnoId !== sugerencia.turnoId));
  };

  const confirmarTodas = () => {
    setAsignacionesConfirmadas(prev => [...prev, ...sugerenciasIA]);
    setSugerenciasIA([]);
  };

  const aplicarAsignaciones = async () => {
    if (asignacionesConfirmadas.length === 0) {
      setError('No hay asignaciones confirmadas para aplicar');
      return;
    }

    setLoading(true);
    setProcessingStep('Aplicando asignaciones...');

    try {
      console.log('Asignaciones a aplicar:', asignacionesConfirmadas);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (onAsignacionComplete) {
        onAsignacionComplete(asignacionesConfirmadas);
      }
      
      setAsignacionesConfirmadas([]);
      setSugerenciasIA([]);
      setIaStatus('idle');
      
    } catch (err) {
      console.error('Error aplicando asignaciones:', err);
      setError('Error al aplicar asignaciones: ' + err.message);
    } finally {
      setLoading(false);
      setProcessingStep('');
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  return (
    <Box sx={{ 
      backgroundColor: '#fff',
      borderRadius: 2,
      boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        background: 'linear-gradient(135deg, rgb(70, 120, 195) 0%, rgb(50, 90, 160) 100%)',
        color: '#fff',
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToyIcon />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Asignación Inteligente con IA
          </Typography>
          <Chip 
            label="MVP" 
            size="small" 
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.2)', 
              color: '#fff',
              fontWeight: 600
            }} 
          />
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <EventIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Turnos Sin Asignar
                  </Typography>
                </Box>
                
                {loadingVigilantes ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <>
                    <Typography variant="h3" color="primary" sx={{ mb: 1 }}>
                      {turnosSinAsignar.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      turnos pendientes de asignación
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonIcon color="success" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Vigilantes Disponibles
                  </Typography>
                </Box>
                
                {loadingVigilantes ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <>
                    <Typography variant="h3" color="success.main" sx={{ mb: 1 }}>
                      {vigilantesDisponibles.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {vigilantesDisponibles.filter(v => v.tieneServicioActual).length} con servicio asignado
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CheckCircleIcon color="info" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Asignaciones Confirmadas
                  </Typography>
                </Box>
                
                <Typography variant="h3" color="info.main" sx={{ mb: 1 }}>
                  {asignacionesConfirmadas.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  listas para aplicar
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={generarSugerenciasIA}
            disabled={loading || loadingVigilantes || turnosSinAsignar.length === 0}
            sx={{ 
              background: 'linear-gradient(135deg, rgb(70, 120, 195) 0%, rgb(50, 90, 160) 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, rgb(60, 100, 175) 0%, rgb(40, 80, 140) 100%)'
              }
            }}
          >
            {loading ? 'Procesando...' : 'Generar Sugerencias con IA'}
          </Button>

          {sugerenciasIA.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<CheckCircleIcon />}
              onClick={confirmarTodas}
              color="success"
            >
              Confirmar Todas ({sugerenciasIA.length})
            </Button>
          )}

          {asignacionesConfirmadas.length > 0 && (
            <Button
              variant="contained"
              startIcon={<AssignmentIndIcon />}
              onClick={aplicarAsignaciones}
              color="success"
              disabled={loading}
            >
              Aplicar Asignaciones ({asignacionesConfirmadas.length})
            </Button>
          )}
        </Box>

        {loading && processingStep && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon fontSize="small" />
              {processingStep}
            </Typography>
          </Box>
        )}

        {sugerenciasIA.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon color="primary" />
              Sugerencias de IA ({sugerenciasIA.length})
            </Typography>
            
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {sugerenciasIA.map((sugerencia, index) => (
                <ListItem
                  key={sugerencia.turnoId}
                  sx={{
                    backgroundColor: '#f8f9fa',
                    borderRadius: 1,
                    mb: 1,
                    border: '1px solid #e9ecef'
                  }}
                  secondaryAction={
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => confirmarAsignacion(sugerencia)}
                      startIcon={<CheckCircleIcon />}
                    >
                      Confirmar
                    </Button>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'rgb(70, 120, 195)' }}>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={600}>
                          {sugerencia.vigilanteNombre}
                        </Typography>
                        <Chip 
                          label={`${sugerencia.confianza}%`} 
                          size="small" 
                          color={sugerencia.confianza >= 90 ? 'success' : 'warning'}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          📅 {formatearFecha(sugerencia.turnoInfo?.fecha)} | ⏰ {sugerencia.turnoInfo?.horaInicio} - {sugerencia.turnoInfo?.horaFin}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          💡 {sugerencia.razon}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {asignacionesConfirmadas.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#e8f5e9' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
              <CheckCircleIcon />
              Asignaciones Confirmadas ({asignacionesConfirmadas.length})
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {asignacionesConfirmadas.map((asig, index) => (
                <Chip
                  key={asig.turnoId}
                  label={`${asig.vigilanteNombre} → ${formatearFecha(asig.turnoInfo?.fecha)}`}
                  color="success"
                  variant="outlined"
                  onDelete={() => {
                    setAsignacionesConfirmadas(prev => prev.filter(a => a.turnoId !== asig.turnoId));
                    setSugerenciasIA(prev => [...prev, asig]);
                  }}
                />
              ))}
            </Box>
          </Paper>
        )}

        {vigilantesDisponibles.length > 0 && iaStatus === 'idle' && (
          <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WorkIcon color="primary" />
              Vigilantes del Servicio
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {vigilantesDisponibles.slice(0, 10).map((vigilante) => (
                <Chip
                  key={vigilante.id}
                  avatar={<Avatar>{vigilante.nombreCompleto?.charAt(0) || '?'}</Avatar>}
                  label={vigilante.nombreCompleto}
                  variant={vigilante.tieneServicioActual ? 'filled' : 'outlined'}
                  color={vigilante.tieneServicioActual ? 'primary' : 'default'}
                  sx={{ 
                    backgroundColor: vigilante.tieneServicioActual ? 'rgb(70, 120, 195)' : undefined,
                    color: vigilante.tieneServicioActual ? '#fff' : undefined
                  }}
                />
              ))}
              {vigilantesDisponibles.length > 10 && (
                <Chip 
                  label={`+${vigilantesDisponibles.length - 10} más`} 
                  variant="outlined" 
                />
              )}
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default AsignacionIA;
